/**
 * SalespersonFilterSheet Section Component
 *
 * Bottom sheet for filtering orders by salesperson on mobile (Figma
 * 14769:96861): drag handle, header with close, scrollable checkbox list,
 * and a Clear all / Confirm footer (Figma 14769:98538). Mobile counterpart
 * of the toolbar's DataTableFacetedFilter — selection is staged locally and
 * applied to the parent only when Confirm is pressed.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { CloseIcon } from '../../../icons';
import { MobilePillButton, MobilePrimaryButton } from '../../../layouts/MobileButtons';
import type { SalespersonFilterSheetProps } from '../types';

export type { SalespersonFilterSheetProps };

export function SalespersonFilterSheet({
  open,
  onOpenChange,
  salespersons,
  selectedIds,
  onSelectionChange,
  title,
  clearAllLabel,
  confirmLabel,
}: SalespersonFilterSheetProps) {
  // Draft selection — toggles and Clear all only mutate this local state.
  // Nothing is applied to the parent (URL/query) until Confirm. Resynced to
  // the applied selection each time the sheet opens, so dismissing without
  // confirming (close button / backdrop) discards the edits.
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);

  useEffect(() => {
    if (open) setDraftIds(selectedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync only on open
  }, [open]);

  const toggle = (id: string) => {
    setDraftIds((prev) =>
      prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideClose
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={cn(
          'flex max-h-sheet-mobile flex-col rounded-t-input border-dinnerplate-white bg-off-white p-0 pb-safe-bottom',
        )}
      >
        <div className="mx-auto mt-2 h-1 w-12 shrink-0 rounded-full bg-grey-light" />
        <SheetHeader className="shrink-0 flex-row items-center justify-between space-y-0 border-b border-dinnerplate-white p-6 text-left">
          <SheetTitle className="font-inter text-sm font-semibold leading-normal text-off-black">
            {title ?? 'Salesperson'}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Filter orders by salesperson
          </SheetDescription>
          <SheetClose
            className="shrink-0 rounded-xs text-off-black focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black/30"
            aria-label="Close"
          >
            <CloseIcon className="size-4" />
          </SheetClose>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-6 py-2 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          {salespersons.map((salesperson) => {
            const id = String(salesperson.id);
            return (
              <label
                key={salesperson.id}
                htmlFor={`salesperson-filter-${id}`}
                className="flex cursor-pointer items-center gap-4 py-4"
              >
                <Checkbox
                  id={`salesperson-filter-${id}`}
                  checked={draftIds.includes(id)}
                  onCheckedChange={() => toggle(id)}
                />
                <span className="truncate font-inter text-sm leading-normal text-grey">
                  {salesperson.name}
                </span>
              </label>
            );
          })}
        </div>

        <div className="flex shrink-0 gap-3 border-t border-dinnerplate-white p-6">
          <MobilePillButton
            onClick={() => setDraftIds([])}
            className="flex-1 justify-center text-nav"
          >
            {clearAllLabel ?? 'Clear all'}
          </MobilePillButton>
          <MobilePrimaryButton
            onClick={() => {
              onSelectionChange(draftIds);
              onOpenChange(false);
            }}
            className="flex-1 justify-center text-nav"
          >
            {confirmLabel ?? 'Confirm'}
          </MobilePrimaryButton>
        </div>
      </SheetContent>
    </Sheet>
  );
}
